import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";

/*
 * O botão primário é CLARO, e não colorido (doc 06a §3.1): `--texto` de fundo
 * com texto `--breu`. Isso libera o azul esferográfica para significar estado e
 * foco sem competir com ações.
 *
 * `destrutivo` é vermelho contido, e o doc 06a §5 é explícito sobre o texto: o
 * verbo do botão é o verbo do resultado — "Cancelar boleto", nunca "Confirmar".
 */
type Tom = "primario" | "secundario" | "destrutivo";

const TONS: Record<Tom, string> = {
  primario: "bg-texto text-breu hover:bg-white disabled:hover:bg-texto",
  secundario:
    "border border-fio-forte bg-superficie-2 text-texto hover:bg-fio disabled:hover:bg-superficie-2",
  destrutivo: "bg-falha/15 border border-falha/40 text-falha hover:bg-falha/25",
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  tom?: Tom;
  carregando?: boolean;
  children: ReactNode;
}

export function Botao({
  tom = "primario",
  carregando = false,
  disabled,
  className = "",
  children,
  ...resto
}: Props) {
  return (
    <button
      // `type` explícito: o padrão do HTML é `submit`, e um botão de ação dentro
      // de um formulário o envia sem que ninguém tenha pedido.
      type="button"
      disabled={disabled === true || carregando}
      /*
       * `aria-busy` porque o spinner sozinho não diz nada a quem usa leitor de
       * tela — e o doc 06a §7 exige que nenhuma informação seja transmitida só
       * por elemento visual.
       */
      aria-busy={carregando}
      className={[
        "inline-flex h-8 items-center justify-center gap-2 rounded-controle px-3",
        "text-corpo font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        TONS[tom],
        className,
      ].join(" ")}
      {...resto}
    >
      {carregando && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
      {children}
    </button>
  );
}
